import { useEffect, useMemo, useRef } from "react";

import { Link } from "@tanstack/react-router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import {
  getNeedsAttention as getExternalNeedsAttention,
  getNeedsAttentionQueryKey as getExternalNeedsAttentionQueryKey,
} from "@synthesis/shared-services/external/application";
import {
  getQueryKey as getExternalUiStateQueryKey,
  useGet as useGetExternalUiState,
  useSave as useSaveExternalUiState,
} from "@synthesis/shared-services/external/ui-state";
import {
  getNeedsAttention as getInternalNeedsAttention,
  getNeedsAttentionQueryKey as getInternalNeedsAttentionQueryKey,
} from "@synthesis/shared-services/internal/application";
import {
  getQueryKey as getInternalUiStateQueryKey,
  useGet as useGetInternalUiState,
  useSave as useSaveInternalUiState,
} from "@synthesis/shared-services/internal/ui-state";
import { Accordion, Counter, ScrollArea, Tooltip } from "@synthesis/shared-ui";
import { cn, useSearch } from "@synthesis/shared-utils";

import { formatApplicationNumber } from "../../../shared/utils/application.utils";
import { ApplicationProcessingStateBadge, ApplicationStatusBadge } from "../../../components/application/application-status";
import { APPLICATION_PROCESSING_STATE_LABEL, getApplicationProcessingState, toNumber, type ApplicationsTableItem } from "../applications-page.lib";
import { APPLICATION_STATUS_FALLBACK_LABEL, type ApplicationStatus } from "../applications-page.status";
import { useUserInfo } from "../../../shared/hooks/use-user-info";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import { useClientType } from "../../../shared/hooks/use-client-type";
import { IconFileAttention, IconLoader } from "@synthesis/shared-icons";

type NeedsAttentionItem = ApplicationsTableItem;

const ATTENTION_PAGE_SIZE = 10;
const ATTENTION_SCROLL_VISIBLE_COUNT = 5;
const ATTENTION_ITEM_HEIGHT_PX = 48;
const ATTENTION_ITEM_GAP_PX = 8;
const ATTENTION_ITEM_ESTIMATED_SIZE = ATTENTION_ITEM_HEIGHT_PX + ATTENTION_ITEM_GAP_PX;
const ATTENTION_LIST_SCROLL_MAX_HEIGHT_CLASS = "max-h-[calc(5*3rem+4*0.5rem)]";
const ATTENTION_LIST_SKELETON_COUNT = 5;
const NEEDS_ATTENTION_EXPANDED_KEY = "needs_attention_expanded";
const DEFAULT_NEEDS_ATTENTION_EXPANDED = true;

const parseNeedsAttentionExpanded = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  return DEFAULT_NEEDS_ATTENTION_EXPANDED;
};

const AttentionItemTitle = ({ title }: { title: string }) => (
  <Tooltip description={title} arrowSide="top">
    <div className="subtitle-base text-type-minor min-w-0 flex-1 truncate">{title}</div>
  </Tooltip>
);

const AttentionStatusBadge = ({ item }: { item: NeedsAttentionItem }) => {
  const clientType = useClientType();
  const status = item.status as ApplicationStatus | undefined;

  if (!status) {
    return <span className="text-type-minor">—</span>;
  }

  const record = item as Record<string, unknown>;

  return (
    <span className="shrink-0">
      <ApplicationStatusBadge
        contour={clientType ?? "internal"}
        label={(typeof record.displayStatus === "string" ? record.displayStatus : undefined) ?? APPLICATION_STATUS_FALLBACK_LABEL[status]}
        status={status}
      />
    </span>
  );
};

const AttentionProcessingBadge = ({ item }: { item: NeedsAttentionItem }) => {
  // needs-attention returns a trimmed payload; for operators the block only lists export-required applications.
  const state = getApplicationProcessingState(item) ?? "REQUIRES_EXPORT";

  return (
    <span className="shrink-0">
      <ApplicationProcessingStateBadge label={APPLICATION_PROCESSING_STATE_LABEL[state]} processingState={state} />
    </span>
  );
};

const EmptyAttentionState = () => (
  <div className="bg-neutrals-white flex min-h-40 flex-col items-center justify-center gap-2 rounded-2xl px-4 py-8 text-center">
    <IconFileAttention className="text-system-error-50 size-12" />
    <p className="subtitle-base text-type-common">Нет заявок, требующих внимания</p>
    <p className="caption-xs text-type-minor">Заявки, требующие внимания, будут отображаться тут</p>
  </div>
);

const AttentionListSkeleton = () => (
  <ul aria-busy="true" aria-label="Загрузка заявок" className="flex w-full flex-col">
    {Array.from({ length: ATTENTION_LIST_SKELETON_COUNT }).map((_, index) => (
      <li key={index} className="pb-2">
        <div className="skeleton h-12 w-full rounded-2xl" />
      </li>
    ))}
  </ul>
);

const getNeedsAttentionNextPageParam = (
  lastPage: { content?: unknown[] | null; page?: number | null; totalPages?: number | null },
  _allPages: unknown[],
  lastPageParam: number,
) => {
  const page = lastPage.page ?? lastPageParam;
  const totalPages = lastPage.totalPages;

  if (totalPages != null) {
    return page + 1 < totalPages ? page + 1 : undefined;
  }

  const content = lastPage.content ?? [];
  return content.length === ATTENTION_PAGE_SIZE ? lastPageParam + 1 : undefined;
};

type AttentionListProps = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isOperator: boolean;
  items: NeedsAttentionItem[];
  onScrollEnd: () => void;
  shouldScroll: boolean;
};

const AttentionList = ({ hasNextPage, isFetchingNextPage, isOperator, items, onScrollEnd, shouldScroll }: AttentionListProps) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const scrollEndRef = useRef(onScrollEnd);
  scrollEndRef.current = onScrollEnd;

  const virtualizer = useVirtualizer({
    count: hasNextPage || isFetchingNextPage ? items.length + 1 : items.length,
    estimateSize: () => ATTENTION_ITEM_ESTIMATED_SIZE,
    getItemKey: (index) => items[index]?.id ?? "attention-list-loader",
    getScrollElement: () => parentRef.current,
    overscan: 3,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItemIndex = virtualItems.at(-1)?.index;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && lastVirtualItemIndex === items.length) {
      scrollEndRef.current();
    }
  }, [hasNextPage, isFetchingNextPage, items.length, lastVirtualItemIndex]);

  return (
    <ScrollArea
      ref={parentRef}
      direction="vertical"
      rootClassName={cn("w-full min-w-0 overflow-x-hidden", shouldScroll && ATTENTION_LIST_SCROLL_MAX_HEIGHT_CLASS)}
      className={cn("w-full min-w-0 overflow-x-hidden", shouldScroll && ATTENTION_LIST_SCROLL_MAX_HEIGHT_CLASS, shouldScroll && "pr-1")}
    >
      <ul className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualItems.map((virtualItem) => {
          if (virtualItem.index === items.length) {
            return (
              <li
                key={virtualItem.key}
                className="absolute left-0 top-0 w-full"
                data-index={virtualItem.index}
                style={{ transform: `translateY(${virtualItem.start}px)` }}
              >
                <div className="flex h-12 items-center justify-center">
                  <IconLoader className="size-6 animate-spin" />
                </div>
              </li>
            );
          }

          const item = items[virtualItem.index];

          if (!item) {
            return null;
          }

          const title = item.title?.trim() || "—";

          return (
            <li
              key={virtualItem.key}
              className="absolute left-0 top-0 w-full pb-2"
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{ transform: `translateY(${virtualItem.start}px)` }}
            >
              <div className="bg-neutrals-white min-w-0 overflow-hidden rounded-2xl">
                <Link
                  to="/applications/$id"
                  params={{ id: item.id ?? "" }}
                  className="hover:bg-neutrals-99 grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 overflow-hidden px-3 py-2 transition-colors"
                >
                  <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                    <p className="paragraph-base shrink-0">
                      <span className="text-type-light">№ Заявки </span>
                      <span className="text-type-minor">{formatApplicationNumber(item.number)}</span>
                    </p>
                    <AttentionItemTitle title={title} />
                  </div>
                  {isOperator ? <AttentionProcessingBadge item={item} /> : <AttentionStatusBadge item={item} />}
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </ScrollArea>
  );
};

export const AttentionSection = () => {
  const queryClient = useQueryClient();
  const userInfo = useUserInfo();
  const roles = useUserRoles();
  const clientType = useClientType();
  const [search] = useSearch();
  const isOperator = (clientType === "external" && roles.isExternalOperator) || (clientType === "internal" && roles.isInternalOperator);
  const hasUser = Boolean(userInfo);
  const getNeedsAttention = clientType === "internal" ? getInternalNeedsAttention : getExternalNeedsAttention;
  const getNeedsAttentionQueryKey = clientType === "internal" ? getInternalNeedsAttentionQueryKey : getExternalNeedsAttentionQueryKey;
  const searchQuery = search || undefined;
  const needsAttentionQueryParams = { search: searchQuery, size: ATTENTION_PAGE_SIZE };

  const needsAttentionQuery = useInfiniteQuery({
    enabled: hasUser && clientType != null,
    queryKey: [...getNeedsAttentionQueryKey({ query: needsAttentionQueryParams }), "infinite"],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getNeedsAttention({
        query: { ...needsAttentionQueryParams, page: pageParam },
        signal,
      });
      return data;
    },
    initialPageParam: 0,
    getNextPageParam: getNeedsAttentionNextPageParam,
  });

  const externalUiStateQuery = useGetExternalUiState(
    { path: { key: NEEDS_ATTENTION_EXPANDED_KEY } },
    {
      query: { enabled: hasUser && clientType === "external" },
    },
  );
  const internalUiStateQuery = useGetInternalUiState(
    { path: { key: NEEDS_ATTENTION_EXPANDED_KEY } },
    {
      query: { enabled: hasUser && clientType === "internal" },
    },
  );

  const saveExternalUiState = useSaveExternalUiState();
  const saveInternalUiState = useSaveInternalUiState();

  const uiStateQuery = clientType === "internal" ? internalUiStateQuery : externalUiStateQuery;
  const saveUiState = clientType === "internal" ? saveInternalUiState : saveExternalUiState;
  const getUiStateQueryKey = clientType === "internal" ? getInternalUiStateQueryKey : getExternalUiStateQueryKey;

  const items = useMemo(
    () => (needsAttentionQuery.data?.pages.flatMap((page) => page.content ?? []) ?? []) as NeedsAttentionItem[],
    [needsAttentionQuery.data?.pages],
  );
  const isAttentionLoading = needsAttentionQuery.isPending;
  const totalElements = needsAttentionQuery.data?.pages[0]?.totalElements;
  const attentionCount = totalElements != null ? toNumber(totalElements) : items.length;
  const shouldScrollAttentionList = attentionCount > ATTENTION_SCROLL_VISIBLE_COUNT;
  const uiStateQueryKey = getUiStateQueryKey({ path: { key: NEEDS_ATTENTION_EXPANDED_KEY } });
  const isExpanded = parseNeedsAttentionExpanded(uiStateQuery.data?.value ?? DEFAULT_NEEDS_ATTENTION_EXPANDED);

  const handleExpandedChange = (open: boolean) => {
    const previous = queryClient.getQueryData(uiStateQueryKey);

    queryClient.setQueryData(uiStateQueryKey, {
      key: NEEDS_ATTENTION_EXPANDED_KEY,
      value: open,
    });

    saveUiState.mutate(
      { path: { key: NEEDS_ATTENTION_EXPANDED_KEY }, body: { value: open } },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(uiStateQueryKey, data);
        },
        onError: () => {
          queryClient.setQueryData(uiStateQueryKey, previous);
        },
      },
    );
  };

  return (
    <Accordion
      title={
        <span className="flex items-center gap-2">
          <span className="subtitle-sm text-system-error-50">Требуют внимания</span>
          {!isAttentionLoading && attentionCount > 0 ? (
            <span className="shrink-0">
              <Counter count={attentionCount} />
            </span>
          ) : null}
        </span>
      }
      open={isExpanded}
      onOpenChange={handleExpandedChange}
      className={cn(
        "bg-neutrals-95 w-full min-w-0 shrink-0 overflow-x-hidden rounded-[14px] border-none",
        "[&_button]:px-3 [&_button]:py-2",
        "[&>div:last-child]:bg-transparent [&>div:last-child]:px-2 [&>div:last-child]:pb-2 [&>div:last-child]:pt-0",
      )}
    >
      {isAttentionLoading ? (
        <AttentionListSkeleton />
      ) : items.length > 0 ? (
        <AttentionList
          hasNextPage={needsAttentionQuery.hasNextPage}
          isFetchingNextPage={needsAttentionQuery.isFetchingNextPage}
          isOperator={isOperator}
          items={items}
          onScrollEnd={() => {
            void needsAttentionQuery.fetchNextPage();
          }}
          shouldScroll={shouldScrollAttentionList}
        />
      ) : (
        <EmptyAttentionState />
      )}
    </Accordion>
  );
};
